// 9fbef606107a605d69c0edbcd8029e5d
import {
  validateBossEligibility,
  validateBopisEligibility,
} from '@tcp/core/src/components/common/organisms/ProductPickup/util';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty.js';
import { getIsAllowOnlineProductPickupEnabled } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import CARTPAGE_CONSTANTS from '../../../CartItemTile.constants';
import CONSTANTS from '../../../../Checkout/Checkout.constants';
import { currencyConversion } from '../../../utils/utils';
import CartItemTileLabels, { GLOBAL_LABELS } from './CartItemTile.labels';

/**
 * @function noBossBopisMessage Checks for online only or clearance messages for BOSS/BOPIS items
 * @return {Object}
 * @memberof CartItemTile
 */
export const noBossBopisMessage = (props) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  const {
    productDetail: {
      miscInfo: { isOnlineOnly, clearanceItem, storeSpecials },
      productInfo: { tcpStyleType },
    },
    isBopisClearanceProductEnabled,
    isBossClearanceProductEnabled,
    showBopisTileBasedOnStoreSpecials,
  } = props;

  let noBopisMessage = null;
  let noBossMessage = null;
  // BOPIS online only check

  const showErrorMessage = showBopisTileBasedOnStoreSpecials
    ? isOnlineOnly && storeSpecials !== undefined && !storeSpecials
    : isOnlineOnly;
  if (showErrorMessage) {
    noBopisMessage = renderLabel(
      cartItemTile?.lbl_cartTile_notAvailableOnlineOnly,
      'lbl_cartTile_notAvailableOnlineOnly'
    );
  } else if (clearanceItem && !isBopisClearanceProductEnabled) {
    // BOPIS clearance check
    noBopisMessage = renderLabel(
      cartItemTile?.lbl_cartTile_notAvailableClearanceItem,
      'lbl_cartTile_notAvailableClearanceItem'
    );
  }

  // BOSS clearance check
  if (clearanceItem && !isBossClearanceProductEnabled) {
    noBossMessage = renderLabel(
      cartItemTile?.lbl_cartTile_notAvailableClearanceItem,
      'lbl_cartTile_notAvailableClearanceItem'
    );
  }

  if (
    getIsAllowOnlineProductPickupEnabled() &&
    tcpStyleType !== '0005' &&
    tcpStyleType !== '0006'
  ) {
    noBopisMessage = null;
  }

  return { noBopisMessage, noBossMessage };
};

export const isEcomOrder = (orderType) => {
  return (
    orderType === null ||
    orderType === CONSTANTS.ORDER_ITEM_TYPE.ECOM ||
    orderType === CONSTANTS.ORDER_ITEM_TYPE.PURE_ECOM
  );
};

export const isBopisOrder = (orderType) => orderType === CONSTANTS.ORDER_ITEM_TYPE.BOPIS;

export const isBossOrder = (orderType) => orderType === CONSTANTS.ORDER_ITEM_TYPE.BOSS;

/**
 * @function checkBOSSDisabled
 * @param {bool} isBossEnabled Represents Country/State level kill switch
 * @param {bool} isEcomSoldout Represents whether the product is sold out
 * @param {bool} isBOSSOrder Represent BOSS item
 * @memberof CartItemTile
 */
export const checkBOSSDisabled = (props, isBossEnabled, isEcomSoldout, isBOSSOrder) => {
  const {
    productDetail: {
      miscInfo: { availability },
    },
    productDetail: { miscInfo },
    isBossClearanceProductEnabled,
    isRadialInventoryEnabled,
  } = props;
  return (
    !validateBossEligibility({
      isBossClearanceProductEnabled,
      isBossEnabled,
      miscInfo,
    }) ||
    (isRadialInventoryEnabled
      ? !miscInfo.isInventoryAvailBOSS ||
        (isBOSSOrder && availability !== CARTPAGE_CONSTANTS.AVAILABILITY.OK)
      : isEcomSoldout)
  );
};

/**
 * @function checkBOPISDisabled
 * @param {bool} isBopisEnabled Represents Country/State level kill switch
 * @param {bool} isEcomSoldout Represents whether the product is sold out
 * @param {bool }isBOPISOrder Represent BOPIS item
 * @memberof CartItemTile
 */
export const checkBOPISDisabled = (props, isBopisEnabled, isEcomSoldout, isBOPISOrder) => {
  const {
    productDetail: {
      miscInfo: { isOnlineOnly, availability, storeSpecials },
      itemInfo: { isGiftItem },
      productInfo: { tcpStyleType },
    },
    productDetail: { miscInfo },
    isBopisClearanceProductEnabled,
  } = props;
  const storeSpecialsProd = storeSpecials ? !storeSpecials : isOnlineOnly;
  if (
    getIsAllowOnlineProductPickupEnabled() &&
    tcpStyleType !== '0005' &&
    tcpStyleType !== '0006'
  ) {
    return false;
  }
  return (
    !validateBopisEligibility({
      isBopisClearanceProductEnabled,
      isBopisEnabled,
      miscInfo,
    }) ||
    (isBOPISOrder && availability !== CARTPAGE_CONSTANTS.AVAILABILITY.OK) ||
    storeSpecialsProd ||
    isEcomSoldout ||
    isGiftItem
  );
};

/**
 * @function checkBossBopisDisabled
 * @param {bool} isBossEnabled Represents Country/State level kill switch
 * @param {bool} isBopisEnabled Represents Country/State level kill switch
 * @param {bool} isEcomSoldout Represents whether the product is sold out
 * @param {bool} isBOSSOrder Represent BOSS item
 * @param {bool }isBOPISOrder Represent BOPIS item
 * @memberof CartItemTile
 */
export const checkBossBopisDisabled = (
  props,
  isBossEnabled,
  isBopisEnabled,
  isEcomSoldout,
  isBOSSOrder,
  isBOPISOrder
) => {
  const bossDisabled = checkBOSSDisabled(props, isBossEnabled, isEcomSoldout, isBOSSOrder);
  const bopisDisabled = checkBOPISDisabled(props, isBopisEnabled, isEcomSoldout, isBOPISOrder);
  return { bossDisabled, bopisDisabled };
};

export const showRadioButtons = ({
  isEcomSoldout,
  isECOMOrder,
  isBossEnabled,
  isBopisEnabled,
  store,
}) => {
  return (!isEcomSoldout || isECOMOrder) && (isBossEnabled || isBopisEnabled || !!store);
};

export const isSoldOut = (availability) => availability === CARTPAGE_CONSTANTS.AVAILABILITY.SOLDOUT;

export const getBossBopisFlags = (props, brand) => {
  const { [`isBossEnabled${brand}`]: isBossEnabled, [`isBopisEnabled${brand}`]: isBopisEnabled } =
    props;

  return {
    isBossEnabled,
    isBopisEnabled,
  };
};

export const hideEditBossBopis = (isBOSSOrder, bossDisabled, isBOPISOrder, bopisDisabled) => {
  return (isBOSSOrder && bossDisabled) || (isBOPISOrder && bopisDisabled);
};

/**
 * @function getBOSSUnavailabilityMessage Get Boss Unavailability messages
 * @param {bool} bossDisabled Represents if the boss option should be disabled or not
 * @param {string} noBossMessage Represents the online only products or clearance disabled products message.
 * @param {string} availability Represents status of the availability
 * @returns {string} Unavailable message string
 * @memberof CartItemTile
 */
export const getBOSSUnavailabilityMessage = (
  bossDisabled,
  noBossMessage,
  availability,
  isSinglePageCheckout
) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  let unavailableMessage = '';
  const { UNAVAILABLE, REQ_QTY_UNAVAILABLE, BOSSINELIGIBLE, OK } = CARTPAGE_CONSTANTS.AVAILABILITY;
  /* istanbul ignore else */
  if (bossDisabled || (!!noBossMessage && !isSinglePageCheckout) || availability !== OK) {
    switch (availability) {
      case UNAVAILABLE:
        unavailableMessage = renderLabel(
          cartItemTile?.lbl_cartTile_bossUnavailable,
          'lbl_cartTile_bossUnavailable'
        );
        break;
      case REQ_QTY_UNAVAILABLE:
        unavailableMessage = renderLabel(
          cartItemTile?.lbl_cartTile_bossReqQtyUnavailable,
          'lbl_cartTile_bossInEligible'
        );
        break;
      case BOSSINELIGIBLE:
        unavailableMessage = renderLabel(
          cartItemTile?.lbl_cartTile_bossInEligible,
          'lbl_cartTile_bossInEligible'
        );
        break;
      default:
        unavailableMessage = cartItemTile?.lbl_cartTile_bossUnavailable;
    }
  }
  return unavailableMessage;
};

/**
 * @function getBOPISUnavailabilityMessage Get BOPIS Unavailability messages
 * @param {bool} bopisDisabled Represents if the bopis option should be disabled or not
 * @param {string} noBopisMessage Represents the online only products or clearance disabled products message.
 * @param {string} availability Represents status of the availability
 * @returns {string} Unavailable message string
 * @memberof CartItemTile
 */
export const getBOPISUnavailabilityMessage = (
  bopisDisabled,
  noBopisMessage,
  availability,
  isSinglePageCheckout
) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  const { UNAVAILABLE, OK } = CARTPAGE_CONSTANTS.AVAILABILITY;
  let unavailableMessage = '';
  /* istanbul ignore else */
  if (bopisDisabled || (!!noBopisMessage && !isSinglePageCheckout) || availability !== OK) {
    switch (availability) {
      case UNAVAILABLE:
        unavailableMessage = renderLabel(
          cartItemTile?.lbl_cartTile_bopisUnavailable,
          'lbl_cartTile_bopisUnavailable'
        );
        break;
      default:
        unavailableMessage = renderLabel(
          cartItemTile?.lbl_cartTile_bossUnavailable,
          'lbl_cartTile_bossUnavailable'
        );
    }
  }
  return unavailableMessage;
};

/**
 * @function getSTHUnavailabilityMessage
 * @param {string} availability Represents status of the availability
 * @memberof CartItemTile
 */
export const getSTHUnavailabilityMessage = (availability) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  return availability !== CARTPAGE_CONSTANTS.AVAILABILITY.OK
    ? renderLabel(cartItemTile?.lbl_cartTile_ecomUnavailable, 'lbl_cartTile_ecomUnavailable')
    : '';
};

export const isCurrencyExchangeAvailable = (currencyExchange) =>
  currencyExchange && currencyExchange.length;

export const getPrices = ({ productDetail, currencyExchange }) => {
  let { listPrice, wasPrice, salePrice, price } = productDetail.itemInfo;
  const isCurrencyExchange = isCurrencyExchangeAvailable(currencyExchange);
  // Cart item tile prices
  salePrice =
    isCurrencyExchange && salePrice
      ? currencyConversion(salePrice, currencyExchange[0])
      : salePrice;
  wasPrice =
    isCurrencyExchange && wasPrice ? currencyConversion(wasPrice, currencyExchange[0]) : wasPrice;

  // SFL prices
  listPrice =
    isCurrencyExchange && listPrice
      ? currencyConversion(listPrice, currencyExchange[0])
      : listPrice;
  price = isCurrencyExchange && price ? currencyConversion(price, currencyExchange[0]) : price;
  return {
    salePrice,
    wasPrice,
    listPrice,
    price,
  };
};

export const getParentWithAccessories = (
  generalProductId = '',
  orderAccessorieItemsParsed = []
) => {
  let primaryAccessories = [];
  let secondaryAccessories = [];
  let allAccessoriesWithParent = [];

  const getAllSubAccessories = (parentProductID = '') => {
    let currentProductAccessories = [];
    if (orderAccessorieItemsParsed && orderAccessorieItemsParsed[parentProductID]) {
      const temp = orderAccessorieItemsParsed[parentProductID]?.miscInfo?.c_accessories || [];
      currentProductAccessories = [...currentProductAccessories, ...temp];
    }
    return currentProductAccessories;
  };

  primaryAccessories = getAllSubAccessories(generalProductId);
  secondaryAccessories = primaryAccessories.map((item) => getAllSubAccessories(item));
  if (secondaryAccessories.length) {
    allAccessoriesWithParent = [...primaryAccessories, ...secondaryAccessories[0]];
  } else allAccessoriesWithParent = primaryAccessories;
  // Removing Duplicates if any
  allAccessoriesWithParent = [...new Set(allAccessoriesWithParent), generalProductId];

  return allAccessoriesWithParent;
};

export const getOrderLevelDiscount = (
  allAccessoriesWithParent = [],
  loyaltyOrderItems = [],
  currentOrderItem
) => {
  let totalDiscount = 0;
  // Adding Order-level discount of accessories and parent product
  if (allAccessoriesWithParent.length > 1) {
    // eslint-disable-next-line no-restricted-syntax
    for (const item of loyaltyOrderItems)
      if (allAccessoriesWithParent.includes(item?.productId)) {
        totalDiscount += item?.priceAfterOrderDiscount - item?.priceAfterItemDiscount;
      }
  }
  // Order-level discount of parent product only
  else {
    totalDiscount =
      currentOrderItem?.priceAfterOrderDiscount - currentOrderItem?.priceAfterItemDiscount;
  }
  return totalDiscount;
};

/**
 * Checks if the given productDetail is a bonus (free) item
 * by matching its name with items in loyaltyOrderItems.
 *
 * @param {Object} productDetail - Product detail object.
 * @param {Array} loyaltyOrderItems - Array of order items.
 * @returns {boolean} - true if the product is a bonus (free), false otherwise.
 */
export const isBonusProduct = (productDetail, loyaltyOrderItems) =>
  !!loyaltyOrderItems?.some(
    (item) =>
      item.productName === productDetail?.itemInfo?.name &&
      item.bonusProductLineItem &&
      productDetail?.itemInfo?.price === 0 &&
      productDetail?.itemInfo?.itemId === item?.itemId
  );
